import { YesNo } from "@/admin/AdminTable.tsx";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { RestrictionLevelBadge } from "@/admin/accounts/RestrictionLevelBadge.tsx";
import { StatusBadge } from "@/admin/accounts/StatusBadge.tsx";
import { menuItemPrivilegeAtom } from "@/admin/privileges.ts";
import { RevenueSection } from "@/admin/users/RevenueSection.tsx";
import { UserPane } from "@/admin/users/UserDetails.tsx";
import { UserLink } from "@/admin/users/UserLink.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow, type DetailsTableRowProps } from "@/components/table/DetailsTableRow.tsx";
import { WSmartTable } from "@/components/table/WTable.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import { baFieldsConfigAtom } from "@/modules/billing/billingAccount/fieldsConfig.ts";
import { baLabels } from "@/modules/billing/billingAccount/fieldsInfo.tsx";
import { countryRegionsConfigQueryAtom } from "@/modules/billing/billingConfigQuery.ts";
import { CountryFlagTitle } from "@/modules/location/CountryFlag.tsx";
import { queryParamNumberCast } from "@/utils/cast.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { BillingAccountHistory, BillingAccountStatus, RestrictionLevel } from "@warrenio/api-spec/spec.oats.gen";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useAtomValue } from "jotai/react";
import { type ReactNode, useState } from "react";

export function AccountsDetailView({ item }: { item: GQAccountItem }) {
    const usersAllowed = useAtomValue(menuItemPrivilegeAtom("users"));

    return (
        <>
            <BillingAccountDetails account={item} />
            {usersAllowed && (
                <>
                    <Separator />
                    <UserPane value={String(item.user_id)} />
                </>
            )}
            <Separator />
            <ContentPane>
                <RevenueSection billingAccountId={item.id} createdAt={item.created} />
            </ContentPane>
            <Separator />
            <AccountsCreditHistory key={item.id} item={item} />
        </>
    );
}

function AccountsCreditHistory({ item }: { item: GQAccountItem }) {
    const { id } = item;
    const api = useApiClient();
    const [creditHistory, setCreditHistory] = useState<BillingAccountHistory[]>();

    async function getBillingAccount() {
        const response = getResponseData(
            await api.GET("/payment/admin/credit", {
                params: { query: { billing_account_id: queryParamNumberCast(id), allow_deleted: true } },
            }),
        );

        setCreditHistory(response);
    }

    const isLoaded = creditHistory !== undefined;

    return (
        <ContentPane className="VStack gap-4">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Balance history</div>

                <WButton
                    isDisabled={item.status === "stopped"}
                    action={() => getBillingAccount()}
                    color="primary"
                    size="md"
                    variant="ghost"
                    icon={isLoaded ? "i-lucide:refresh-cw" : "i-lucide:arrow-down-to-line"}
                >
                    {isLoaded ? "Reload balance history" : "Load balance history"}
                </WButton>
            </div>

            {!!creditHistory && (
                <WSmartTable
                    items={creditHistory}
                    hasViewAllButton
                    head={
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Description</th>
                                <th>Acting User ID</th>
                            </tr>
                        </thead>
                    }
                >
                    {(entry) => {
                        const { acting_user_id, amount, created, description, id } = entry;
                        const isOwner = acting_user_id != null && item.user_id === acting_user_id;
                        const userLabel = isOwner ? <span className="text-muted">Owner</span> : undefined;

                        return (
                            <tr key={id}>
                                <td>{created ? <LongDate date={apiUnixToDate(created)} /> : "-"}</td>
                                <td className={amount > 0 ? "color-success" : "color-error"}>
                                    <CurrencyBalance value={amount} />
                                </td>
                                <td>{description}</td>
                                <td>
                                    <UserLink value={acting_user_id} label={userLabel} />
                                </td>
                            </tr>
                        );
                    }}
                </WSmartTable>
            )}
        </ContentPane>
    );
}

function BillingAccountDetails({ account }: { account: GQAccountItem }) {
    const regionsConfig = useSuspenseQueryAtom(countryRegionsConfigQueryAtom);
    const { id, title, discount_percentage, running_totals, country, state_code, created } = account;
    const ongoing = running_totals?.ongoing ?? 0;

    const stateNameFromList = getOwn(regionsConfig, country)?.states?.find((s) => s.code === state_code)?.name;

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={title ?? ""} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title="Account ID">{id}</DetailsTableRow>

                    <DetailsTableRow title="Account name">{title}</DetailsTableRow>

                    <DetailsTableRow title="Type">
                        <ResourceWithIcon type="billing_account" />
                    </DetailsTableRow>

                    <BFieldRow field="company_name" account={account} />
                    <BFieldRow field="company_reg_code" account={account} />
                    <BFieldRow field="company_vat_number" account={account} />
                    <BFieldRow field="email" account={account} />
                    <BFieldRow field="customer_name" account={account} />
                    <BFieldRow field="customer_phone" account={account} />
                    <BFieldRow field="customer_id_number" account={account} />
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <BFieldRow field="country">
                        <CountryFlagTitle code={country} />
                    </BFieldRow>
                    {stateNameFromList && <BFieldRow field="state_code">{stateNameFromList}</BFieldRow>}
                    <BFieldRow field="county" account={account} title={stateNameFromList ? "County" : undefined} />
                    <BFieldRow field="city" account={account} />
                    <BFieldRow field="address_line1" account={account} />
                    <BFieldRow field="post_index" account={account} />

                    <DetailsTableRow title="Created">
                        <LongDate date={apiUnixToDate(created)} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Ongoing balance" tooltip="Hourly updated usage for current billing period.">
                        <OngoingBalance value={ongoing} bold />
                    </DetailsTableRow>

                    {discount_percentage > 0 && (
                        <DetailsTableRow
                            title="Discount"
                            tooltip="A discount has been applied for all services under this billing account."
                        >
                            <b className="color-success">-{discount_percentage}%</b>
                        </DetailsTableRow>
                    )}

                    <DetailsTableRow title="Status">
                        <StatusBadge value={account.status as BillingAccountStatus | undefined} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Restriction level">
                        <RestrictionLevelBadge value={account.restriction_level as RestrictionLevel} />
                    </DetailsTableRow>

                    {account.suspend_reason && (
                        <DetailsTableRow title="Suspend reason">{account.suspend_reason}</DetailsTableRow>
                    )}

                    <DetailsTableRow title="Deleted">
                        <YesNo value={account.is_deleted ?? false} />
                    </DetailsTableRow>

                    <DetailsTableRow title="Campaign code">{account.referral_code ?? "-"}</DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

interface BFieldRowProps extends DetailsTableRowProps {
    title?: ReactNode;
    children?: ReactNode;
    field: keyof typeof baLabels & keyof GQAccountItem;
    account?: GQAccountItem;
}

function BFieldRow({ title, children, field, account, ...props }: BFieldRowProps) {
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);

    if (!fieldsConfig[field]?.visible) {
        return null;
    }

    let displayedValue: ReactNode;
    if (children === undefined && account != null) {
        const value: unknown = account[field as keyof GQAccountItem];
        displayedValue = value != null && value !== "" ? String(value as any) : "-";
    } else {
        displayedValue = children;
    }

    return (
        <DetailsTableRow title={title ?? baLabels[field]} {...props}>
            {displayedValue}
        </DetailsTableRow>
    );
}
